import React from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { ITaskProgress } from './taskprogress.models';
import { getExecutionHealth, getNextAction } from './taskprogress.logic';

type Props = {
  row: ITaskProgress;
  selected?: boolean;
  onSelect: (row: ITaskProgress, tab?: number) => void;
  onChanged?: () => void;
};

export default class TaskProgressCard extends React.PureComponent<Props> {
  fmtMoney = (v?: number) => {
    const n = Number(v || 0);

    if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;

    return n.toLocaleString();
  };

  stateChipColor = (state?: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch ((state || '').toUpperCase()) {
      case 'APPROVED':
      case 'CLOSED':
        return 'success';
      case 'SUBMITTED':
      case 'UNDER_REVIEW':
        return 'primary';
      case 'RETURNED':
        return 'warning';
      case 'REJECTED':
      case 'BLOCKED':
        return 'error';
      default:
        return 'default';
    }
  };

  render() {
    const { row, selected, onSelect } = this.props;

    const health = getExecutionHealth(row);
    const nextAction = getNextAction(row);
    const isPlannedOnly = !row.rid;

    const physical = health.physical;
    const financial = health.financial;

    return (
      <Paper
        elevation={selected ? 3 : 1}
        sx={{
          p: 2,
          border: selected ? '1px solid' : '1px solid transparent',
          borderColor: selected ? 'primary.main' : 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => onSelect(row)}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {row.tkc}
            </Typography>

            <Typography variant="subtitle1">
              {row.tkn}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {row.atn}
            </Typography>
          </Box>

          <Chip
            size="small"
            color={this.stateChipColor(row.ssc)}
            label={row.ssc || 'State'}
          />
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">Implementation</Typography>
            <Typography variant="caption">{physical.toFixed(1)}%</Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, physical))}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">Financial Disbursement</Typography>
            <Typography variant="caption">{financial.toFixed(1)}%</Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, financial))}
            sx={{ height: 8, borderRadius: 4 }}
          />

          <Typography variant="caption" color="text.secondary">
            Budget: {this.fmtMoney(row.aca)} / {this.fmtMoney(row.pam)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
          {isPlannedOnly && (
            <Chip size="small" color="warning" label="No Progress Yet" />
          )}

          <Chip size="small" label={health.label} />
          <Chip size="small" label={`Variance ${health.variance.toFixed(1)}%`} />
          <Chip size="small" label={`Approved ${Number(row.apr || 0).toFixed(1)}%`} />
          <Chip size="small" label={`Evidence ${row.evc || 0}`} />
          <Chip size="small" label={`Approvals ${row.apc || 0}`} />

          {row.ccc && <Chip size="small" label={row.ccc} />}
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Next Action
          </Typography>

          <Button
            size="small"
            variant={nextAction.mode === 'primary' ? 'contained' : 'outlined'}
            color={nextAction.mode === 'warning' ? 'warning' : 'primary'}
            sx={{ mt: 0.5 }}
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onSelect(row, nextAction.tab);
            }}
          >
            {nextAction.label}
          </Button>
        </Box>
      </Paper>
    );
  }
}